import { InMemoryCache } from '@apollo/client';
import { relayStylePagination } from '@apollo/client/utilities';
import { appState, authStateVar, userStateVar } from 'core/state';

/**
 * Apollo merge function for Fakturownia/Expense list+aggregate queries:
 * each filter combination is its own cache entry (`keyArgs`), and a new
 * response **replaces** the previous one — without this, switching date
 * presets on the dashboard would visually shimmer with the old aggregate
 * before the new one arrives.
 */
const replaceMerge = {
  merge(_existing: unknown, incoming: unknown) {
    return incoming;
  },
};

export const cache: InMemoryCache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        appState: {
          read() {
            return appState();
          },
        },
        authState: {
          read() {
            return authStateVar();
          },
        },
        userState: {
          read() {
            return userStateVar();
          },
        },
        // Доска вакансий (ТЗ 0035). Политика написана руками, а не через
        // `relayStylePagination`: тот пересобирает `pageInfo` целиком, включая
        // `startCursor`/`hasPreviousPage`, которых у `VacancyPageInfo` нет, —
        // и роняет `totalCount`, ради которого доска и показывает «Найдено».
        careerVacancyBoard: {
          keyArgs: [
            'status',
            'directionId',
            'search',
            'createdFrom',
            'createdTo',
            'freshness',
            'hideArchived',
            'source',
            'hasDuplicates',
            'orderBy',
            'orderDir',
          ],
          merge(existing: any, incoming: any, { args }: { args: any }) {
            // Без курсора это первая страница — перезапрос или смена фильтра.
            // Дописывать её к прежней значило бы задваивать список на каждом
            // фоновом опросе `cache-and-network`.
            if (!args?.after || !existing) return incoming;
            return {
              ...incoming,
              edges: [...(existing.edges ?? []), ...(incoming.edges ?? [])],
            };
          },
        },
        // Лента колокольчика (ТЗ 0068). Политика своя, как у доски вакансий:
        // тип не Relay (нет `pageInfo`), а `relayStylePagination` пересобирал
        // бы его и терял `unreadCount` — то самое число, которым обязан
        // совпадать бейдж.
        //
        // `unreadOnly` в `keyArgs`: вкладки «Все» и «Непрочитанные» — разные
        // популяции, и общий список склеил бы их в одну кашу при переключении.
        staffNotifications: {
          keyArgs: ['unreadOnly', 'kinds', 'maxAgeDays'],
          merge(existing: any, incoming: any, { args }: { args: any }) {
            // Без курсора это первая страница: перезапрос после мутации или
            // фоновый `cache-and-network`. Дописывать её к прежней значило бы
            // задваивать ленту на каждом опросе.
            if (!args?.after || !existing) return incoming;
            return {
              ...incoming,
              edges: [...(existing.edges ?? []), ...(incoming.edges ?? [])],
            };
          },
        },
        allContributions: relayStylePagination(),
        allDeferredPayments: relayStylePagination(),
        allStudents: relayStylePagination(),
        metaStudents: relayStylePagination(),
        allUsersSettings: relayStylePagination(),
        allCourses: relayStylePagination(),
        allGroups: relayStylePagination(['hasZoomAccount']),
        allTeachers: relayStylePagination(),
        studentsForJournal: relayStylePagination(),
        allTeachersApplications: relayStylePagination(),
        metaTeachers: relayStylePagination(),
        allStudentsAchievement: relayStylePagination(),
        // Unified group Slack feed: each filter combo is its own list, and
        // `fetchMore({after})` appends through the relay merge — no manual
        // `updateQuery` concatenation (which raced across filter switches
        // and collapsed paginated pages after a refetch).
        groupMessages: relayStylePagination([
          'groupId',
          'search',
          'unreadOnly',
          'flaggedOnly',
          'unhandledOnly',
          'channelId',
        ]),
        // One dialog's thread (spec 0008): newest-first keyset page per
        // conversation; «показать более ранние» appends via the relay merge.
        conversationThread: relayStylePagination(['conversationId']),

        // Панели переписки (ТЗ 0031). `kinds` ОБЯЗАН быть в ключе: панель
        // Slack и вкладка «Почта» смотрят на одного и того же студента и
        // склеились бы в один список, дописывая друг другу страницы.
        studentConversationsPage: relayStylePagination(['studentId', 'kinds']),
        groupConversationsPage: relayStylePagination(['groupId', 'kinds']),
        teacherConversationsPage: relayStylePagination(['teacherId', 'kinds']),

        // Zoom-раздел (ТЗ 0043). Единственные пагинируемые списки приложения,
        // которые оставались БЕЗ политики: страницы склеивались вручную через
        // `fetchMore({ updateQuery })`, а он строит основу конкатенации из
        // `lastResult.data` — ПРЕДЫДУЩЕГО отрисованного списка, а не списка
        // для текущих переменных. Поэтому при переключении группы дозагрузка,
        // начатая под старым фильтром, дописывала её занятия в список новой, и
        // оператор видел несколько групп разом.
        //
        // ⚠️ В ключе обязаны быть ВСЕ аргументы-фильтры: пропущенный вернёт ту
        // же болезнь молча. `first`/`after` в ключ не идут — это и есть
        // страницы, их склеивает relay-merge.
        allZoomLessons: relayStylePagination([
          'dateFrom',
          'dateTo',
          'groupId',
          'courseId',
          'status',
        ]),
        allZoomRecordings: relayStylePagination([
          'dateFrom',
          'dateTo',
          'accountId',
          'groupId',
          'orphanOnly',
          'tooShort',
          'showDeleted',
        ]),

        // Dashboard communications browser: same deal — each filter combo is
        // its own list and `fetchMore({after})` appends via the relay merge.
        conversationsBrowser: relayStylePagination([
          'search',
          'groupId',
          'studentId',
          'dateFrom',
          'dateTo',
          'unhandledOnly',
          'kinds',
        ]),

        // Fakturownia / Expenses — pagination is page/perPage based, not
        // Relay cursors, and we want a fresh response per filter combo.
        //
        // `page`/`perPage` MUST be part of the key: the useAll* drains walk
        // pages with `cache-first` (spec 0008 round 2), and without the page
        // in the key every page-N request cache-hits page 1's entry — the
        // batch never comes back short and the walk loops forever, freezing
        // the tab (found on /finances/invoices with ≥200 rows in the window).
        fakturowniaInvoices: {
          keyArgs: [
            'direction',
            'status',
            'matchStatus',
            'kind',
            // Legacy student/group filters (still served by the backend
            // as fallbacks until the RemoveField migration ships).
            'studentId',
            'groupId',
            // Phase 13: canonical Agreement chain filter.
            'agreementId',
            'dateFrom',
            'dateTo',
            'search',
            'overdueOnly',
            'parentId',
            // ТЗ 0004: reconcile queue / blocked-provision chips.
            'needsLinkOnly',
            'provisionBlockedOnly',
            'page',
            'perPage',
          ],
          ...replaceMerge,
        },
        fakturowniaInvoiceAggregates: {
          keyArgs: [
            'direction',
            'studentId',
            'groupId',
            'dateFrom',
            'dateTo',
            'groupBy',
          ],
          ...replaceMerge,
        },
        expenses: {
          keyArgs: [
            'dateFrom',
            'dateTo',
            'source',
            'categorySlug',
            // recurrence/status were missing pre-round-2 — harmless under
            // network-only, but cache-first would serve another filter's
            // entry on a recurrence/status switch.
            'recurrence',
            'status',
            'teacherId',
            'groupId',
            'legalEntityId',
            // Same page-walk termination requirement as fakturowniaInvoices
            // above — page must key the entry or cache-first loops forever.
            'page',
            'perPage',
          ],
          ...replaceMerge,
        },
        expenseAggregates: {
          keyArgs: ['dateFrom', 'dateTo', 'groupBy', 'teacherId'],
          ...replaceMerge,
        },
      },
    },
    // Cache normalisation: Apollo's default `__typename:id` already works
    // for every Fakturownia/Expense type — `id` is selected in every place
    // that fetches them, including embedded references (`parentInvoice`,
    // `relatedFakturowniaInvoice`, `category`). We tried `keyFields:['pk']`
    // briefly, but embedded refs and mutation payloads skip `pk`, which
    // crashes the cache writer with «Missing field 'pk'» (see Phase 3 fix).
    //
    // Defensive merges: when a query selects `student.user { firstName … }`
    // but forgets `id`, Apollo can't normalise the embedded `CustomUserType`
    // and either drops the existing entity or warns «Cache data may be lost».
    // A field-by-field merge keeps both shapes accumulating into one object
    // instead of replacing the previous projection. Same story for the
    // mirror relations on `TeacherType`. We've now selected `id` in every
    // `user {}` block (`PaginatedStudents`, `FakturowniaInvoices`, …) so the
    // merge fires only as a safety net for future regressions.
    StudentType: {
      fields: {
        user: {
          merge(existing = {}, incoming) {
            return { ...existing, ...incoming };
          },
        },
      },
    },
    TeacherType: {
      fields: {
        user: {
          merge(existing = {}, incoming) {
            return { ...existing, ...incoming };
          },
        },
      },
    },
  },
});
